<script setup lang="ts">
import type { SearchResultAssetFragment } from '#graphql-operations'
import type { CarouselApi } from '@layers/ui/app/components/ui/carousel'
import { AssetType } from '#graphql-operations'

const props = defineProps<{
  assets: (SearchResultAssetFragment & { alt: string })[]
  assetIndex: number
}>()

const emit = defineEmits<{
  (e: 'update:asset-index', index: number): void
}>()

const { t } = useI18n()

// Video state tracking
const videoStates = ref(new Map<number, { timestamp: number, wasPlaying: boolean }>())
const playingVideos = ref(new Set<string>())

// Carousel state
const selectedIndex = ref(0)
const dialogSelectedIndex = ref(0)
const dialogOpen = ref(false)

// Video element refs using Map for efficient access
const galleryVideoRefs = ref(new Map<number, HTMLVideoElement>())
const dialogVideoRefs = ref(new Map<number, HTMLVideoElement>())

// Helper to create unique video key
const getVideoKey = (index: number, isDialog: boolean) => `${isDialog ? 'dialog' : 'gallery'}-${index}`

// Video event handlers
function onVideoPlay(index: number, isDialog: boolean) {
  const videoKey = getVideoKey(index, isDialog)
  playingVideos.value.add(videoKey)
}

function onVideoPause(index: number, isDialog: boolean) {
  const videoKey = getVideoKey(index, isDialog)
  playingVideos.value.delete(videoKey)
}

function onVideoEnded(index: number, isDialog: boolean) {
  const videoKey = getVideoKey(index, isDialog)
  playingVideos.value.delete(videoKey)
}

// Carousel API refs
const emblaMainApi = ref<CarouselApi>()
const emblaThumbnailApi = ref<CarouselApi>()
const emblaDialogMainApi = ref<CarouselApi>()
const emblaDialogThumbnailApi = ref<CarouselApi>()

const showNavigation = computed(() => props.assets.length > 1)

// Function refs for dynamic video element tracking
function setGalleryVideoRef(el: HTMLVideoElement | null, index: number) {
  if (el) {
    galleryVideoRefs.value.set(index, el)
  }
  else {
    galleryVideoRefs.value.delete(index)
  }
}

function setDialogVideoRef(el: HTMLVideoElement | null, index: number) {
  if (el) {
    dialogVideoRefs.value.set(index, el)
  }
  else {
    dialogVideoRefs.value.delete(index)
  }
}

// Get video element safely with null checks
function getVideoElement(index: number, isDialog: boolean): HTMLVideoElement | null {
  const refs = isDialog ? dialogVideoRefs.value : galleryVideoRefs.value
  return refs.get(index) || null
}

// Pause all videos except the specified one
function pauseAllVideosExcept(exceptIndex: number | null = null, isDialog: boolean) {
  const refs = isDialog ? dialogVideoRefs.value : galleryVideoRefs.value
  refs.forEach((video, index) => {
    if (index !== exceptIndex && !video.paused) {
      video.pause()
      const videoKey = getVideoKey(index, isDialog)
      playingVideos.value.delete(videoKey)
    }
  })
}

// Toggle video play/pause with state tracking
async function toggleVideo(index: number, isDialog: boolean) {
  const video = getVideoElement(index, isDialog)
  if (!video)
    return

  const videoKey = getVideoKey(index, isDialog)

  // Pause all other videos in both views
  pauseAllVideosExcept(index, isDialog)
  pauseAllVideosExcept(null, !isDialog)

  if (video.paused) {
    try {
      await video.play()
      playingVideos.value.add(videoKey)
      videoStates.value.set(index, {
        timestamp: video.currentTime,
        wasPlaying: true,
      })
    }
    catch (error) {
      console.error('Error playing video:', error)
    }
  }
  else {
    video.pause()
    playingVideos.value.delete(videoKey)
    videoStates.value.set(index, {
      timestamp: video.currentTime,
      wasPlaying: false,
    })
  }
}

// Transfer video state from gallery to dialog
async function transferVideoStateToDialog() {
  await nextTick() // Ensure dialog videos are rendered

  // First, preserve any existing video states (from previous navigation)
  const existingStates = new Map(videoStates.value)

  // Transfer video states
  galleryVideoRefs.value.forEach((galleryVideo, index) => {
    const dialogVideo = getVideoElement(index, true)
    if (!dialogVideo)
      return

    // Get the most recent timestamp - either from current video or saved state
    const savedState = existingStates.get(index)
    const timestamp = savedState?.timestamp || galleryVideo.currentTime

    // Transfer timestamp
    dialogVideo.currentTime = timestamp

    // Check if video is currently playing or if we have a saved state indicating it was playing
    const wasPlaying = !galleryVideo.paused || savedState?.wasPlaying === true

    videoStates.value.set(index, {
      timestamp,
      wasPlaying,
    })

    // Pause gallery video
    if (!galleryVideo.paused) {
      galleryVideo.pause()
    }
  })

  // Resume video in dialog if it was playing
  const currentState = videoStates.value.get(dialogSelectedIndex.value)
  if (currentState?.wasPlaying) {
    const dialogVideo = getVideoElement(dialogSelectedIndex.value, true)
    if (dialogVideo) {
      try {
        await dialogVideo.play()
      }
      catch (error) {
        console.error('Error playing video in dialog:', error)
      }
    }
  }
}

// Transfer video state from dialog to gallery
async function transferVideoStateToGallery() {
  // First, preserve any existing video states (from previous navigation)
  const existingStates = new Map(videoStates.value)

  // Save dialog video states
  dialogVideoRefs.value.forEach((dialogVideo, index) => {
    const savedState = existingStates.get(index)

    // Check if video is currently playing or if we have a saved state indicating it was playing
    const wasPlaying = !dialogVideo.paused || savedState?.wasPlaying === true

    videoStates.value.set(index, {
      timestamp: dialogVideo.currentTime,
      wasPlaying,
    })

    if (!dialogVideo.paused) {
      dialogVideo.pause()
    }
  })

  await nextTick() // Ensure gallery is rendered

  // Apply saved states to gallery videos
  videoStates.value.forEach((state, index) => {
    const galleryVideo = getVideoElement(index, false)
    if (!galleryVideo)
      return

    galleryVideo.currentTime = state.timestamp
  })

  // Resume video in gallery if it was playing in dialog
  const finalState = videoStates.value.get(selectedIndex.value)
  if (finalState?.wasPlaying) {
    const galleryVideo = getVideoElement(selectedIndex.value, false)
    if (galleryVideo) {
      try {
        await galleryVideo.play()
      }
      catch (error) {
        console.error('Error playing video in gallery:', error)
      }
    }
  }
}

// Scroll to specific index with video handling
async function scrollTo(index: number, _updateDialog: boolean = false, source: 'gallery' | 'dialog' | 'thumbnail' = 'gallery') {
  const isDialog = source === 'dialog'
  const currentIndex = isDialog ? dialogSelectedIndex.value : selectedIndex.value

  // Don't do anything if we're already at this index
  if (index === currentIndex)
    return

  // Save state of currently playing video before navigation
  const currentVideo = getVideoElement(currentIndex, isDialog)
  if (currentVideo && !currentVideo.paused) {
    videoStates.value.set(currentIndex, {
      timestamp: currentVideo.currentTime,
      wasPlaying: true,
    })
    currentVideo.pause()
  }

  // Update index and scroll
  if (isDialog) {
    dialogSelectedIndex.value = index
    emblaDialogMainApi.value?.scrollTo(index)
    emblaDialogThumbnailApi.value?.scrollTo(index)
  }
  else {
    selectedIndex.value = index
    emblaMainApi.value?.scrollTo(index)
    emblaThumbnailApi.value?.scrollTo(index)
  }

  emit('update:asset-index', index)

  // Resume video if it was playing before on this slide
  await nextTick()
  const savedState = videoStates.value.get(index)
  if (savedState?.wasPlaying) {
    const newVideo = getVideoElement(index, isDialog)
    if (newVideo) {
      newVideo.currentTime = savedState.timestamp
      try {
        await newVideo.play()
        const videoKey = getVideoKey(index, isDialog)
        playingVideos.value.add(videoKey)
      }
      catch (error) {
        console.error('Error resuming video playback:', error)
        // Update state to not playing if resume fails
        videoStates.value.set(index, {
          timestamp: savedState.timestamp,
          wasPlaying: false,
        })
      }
    }
  }
}

// Handle dialog open/close
async function onDialogOpenChange(open: boolean) {
  dialogOpen.value = open

  if (open) {
    // Opening dialog
    dialogSelectedIndex.value = selectedIndex.value

    await nextTick()
    await transferVideoStateToDialog()
  }
  else {
    // Closing dialog
    selectedIndex.value = dialogSelectedIndex.value
    await transferVideoStateToGallery()
    // Sync gallery carousel to last viewed position
    emblaMainApi.value?.scrollTo(dialogSelectedIndex.value)
    emblaThumbnailApi.value?.scrollTo(dialogSelectedIndex.value)
  }
}

// Initialize carousel event listeners
function initializeCarousels() {
  // Gallery carousel
  if (emblaMainApi.value) {
    emblaMainApi.value.on('select', async () => {
      const newIndex = emblaMainApi.value?.selectedScrollSnap() ?? 0
      if (newIndex !== selectedIndex.value) {
        // Save state of currently playing video before navigation
        const currentVideo = getVideoElement(selectedIndex.value, false)
        if (currentVideo && !currentVideo.paused) {
          videoStates.value.set(selectedIndex.value, {
            timestamp: currentVideo.currentTime,
            wasPlaying: true,
          })
          currentVideo.pause()
        }

        // Update index
        selectedIndex.value = newIndex
        emit('update:asset-index', newIndex)

        // Resume video if it was playing before on this slide
        await nextTick()
        const savedState = videoStates.value.get(newIndex)
        if (savedState?.wasPlaying) {
          const newVideo = getVideoElement(newIndex, false)
          if (newVideo) {
            newVideo.currentTime = savedState.timestamp
            try {
              await newVideo.play()
              const videoKey = getVideoKey(newIndex, false)
              playingVideos.value.add(videoKey)
            }
            catch (error) {
              console.error('Error resuming video playback:', error)
              // Update state to not playing if resume fails
              videoStates.value.set(newIndex, {
                timestamp: savedState.timestamp,
                wasPlaying: false,
              })
            }
          }
        }
      }
    })
  }

  // Dialog carousel
  if (emblaDialogMainApi.value) {
    emblaDialogMainApi.value.on('select', async () => {
      const newIndex = emblaDialogMainApi.value?.selectedScrollSnap() ?? 0
      if (newIndex !== dialogSelectedIndex.value) {
        // Save state of currently playing video before navigation
        const currentVideo = getVideoElement(dialogSelectedIndex.value, true)
        if (currentVideo && !currentVideo.paused) {
          videoStates.value.set(dialogSelectedIndex.value, {
            timestamp: currentVideo.currentTime,
            wasPlaying: true,
          })
          currentVideo.pause()
        }

        // Update index
        dialogSelectedIndex.value = newIndex

        // Resume video if it was playing before on this slide
        await nextTick()
        const savedState = videoStates.value.get(newIndex)
        if (savedState?.wasPlaying) {
          const newVideo = getVideoElement(newIndex, true)
          if (newVideo) {
            newVideo.currentTime = savedState.timestamp || 0
            try {
              await newVideo.play()
              const videoKey = getVideoKey(newIndex, true)
              playingVideos.value.add(videoKey)
            }
            catch (error) {
              console.error('Error resuming video playback in dialog:', error)
              // Update state to not playing if resume fails
              videoStates.value.set(newIndex, {
                timestamp: savedState.timestamp,
                wasPlaying: false,
              })
            }
          }
        }
      }
    })
  }
}

// Initialize carousels when APIs are available
watch([emblaMainApi, emblaDialogMainApi], () => {
  initializeCarousels()
})

// Update selected index when prop changes
watch(() => props.assetIndex, (newIndex) => {
  if (newIndex !== selectedIndex.value) {
    selectedIndex.value = newIndex
    emblaMainApi.value?.scrollTo(newIndex)
    emblaThumbnailApi.value?.scrollTo(newIndex)
  }
})

// Clean up on unmount
onUnmounted(() => {
  // Pause all videos
  galleryVideoRefs.value.forEach(video => video.pause())
  dialogVideoRefs.value.forEach(video => video.pause())
  // Clear refs
  galleryVideoRefs.value.clear()
  dialogVideoRefs.value.clear()
  videoStates.value.clear()
  playingVideos.value.clear()
})
</script>

<template>
  <div class="wfull sm:w-auto">
    <UiCarousel
      class="relative mb0 wfull rd-md md:mb4"
      :opts="{ duration: 20 }"
      @init-api="(val) => emblaMainApi = val"
    >
      <UiCarouselContent>
        <UiCarouselItem v-for="(asset, index) in assets" :key="`gallery-${index}`">
          <div class="relative aspect-square hfull wfull">
            <NuxtImg
              v-if="asset.type === AssetType.IMAGE"
              :src="asset.preview"
              :alt="asset.alt"
              width="640"
              height="640"
              class="aspect-square hfull wfull select-none object-cover object-center lg:rd-md"
              preload
            />
            <video
              v-if="asset.type === AssetType.VIDEO && asset.source"
              :ref="(el) => setGalleryVideoRef(el as HTMLVideoElement | null, index)"
              class="pointer-events-auto absolute inset-0 aspect-square hfull wfull object-cover object-center lg:rd-md"
              :poster="asset.preview"

              muted playsinline controls webkit-playsinline
              controlslist="nodownload noremoteplayback nofullscreen"
              disablepictureinpicture=""
              disableremoteplayback=""
              x-webkit-airplay="deny"
              @click="toggleVideo(index, false)"
              @play="onVideoPlay(index, false)"
              @pause="onVideoPause(index, false)"
              @ended="onVideoEnded(index, false)"
            >
              <source :src="asset.source" :type="asset.mimeType">
            </video>
            <!-- Play button for video items -->
            <button
              v-if="asset.type === AssetType.VIDEO && !playingVideos.has(getVideoKey(index, false))"
              class="absolute left-1/2 top-1/2 z-10 h-16 w-16 flex cursor-pointer items-center justify-center rounded-full bg-black/70 transition-colors -translate-x-1/2 -translate-y-1/2 hover:bg-black/80"
              :aria-label="`Play video for ${asset.alt}`"
              @click.stop="toggleVideo(index, false)"
            >
              <svg
                class="h-6 w-6 text-white"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
              </svg>
            </button>
          </div>
        </UiCarouselItem>
      </UiCarouselContent>
      <template v-if="showNavigation">
        <UiCarouselPrevious class="absolute left-4 top-1/2 z10 h-12 w-12 bg-background">
          <Icon name="i-heroicons:chevron-left" class="text-xl" />
        </UiCarouselPrevious>
        <UiCarouselNext class="absolute right-4 top-1/2 z10 h-12 w-12 bg-background">
          <Icon name="i-heroicons:chevron-right" class="text-xl" />
        </UiCarouselNext>
      </template>

      <slot />
      <div v-if="showNavigation" class="absolute bottom-2 z10 m-auto block w-[100%] overflow-hidden px0.5 px1 sm-hidden">
        <div class="mx-auto w-fit flex justify-center rd-md bg-slate/40 px1.5 py1 space-x-2">
          <button
            v-for="idx in assets.length"
            :key="`dot-${idx - 1}`"
            class="h-1.5 w-1.5 rounded-full transition-colors duration-200"
            :class="(idx - 1) === selectedIndex ? 'bg-black' : 'bg-white'"
            :aria-label="`Go to slide ${idx}`"
            @click="scrollTo(idx - 1, false, 'gallery')"
          />
        </div>
      </div>

      <!-- Dialog for detail view -->
      <UiDialog v-model:open="dialogOpen" @update:open="onDialogOpenChange">
        <UiDialogTrigger as-child>
          <slot name="trigger" />
        </UiDialogTrigger>

        <UiDialogContent class="inset-0 h-full max-w-none w-full translate-x-0 translate-y-0 transform-none border-0 bg-transparent p-0">
          <UiDialogHeader class="sr-only">
            <UiDialogTitle>
              Product Gallery
            </UiDialogTitle>
            <UiDialogDescription>
              Click on the thumbnails to navigate through the gallery.
            </UiDialogDescription>
          </UiDialogHeader>
          <div class="relative min-h-full flex items-center justify-center sm:p8">
            <UiDialogClose class="absolute right-6 top-6 z-50 p-0.25em c-white hover:c-slate-100">
              <Icon name="i-heroicons:x-mark" class="h-6 w-6" />
              <span class="sr-only">{{ t('aria.close.label') }}</span>
            </UiDialogClose>

            <div class="h-full w-full flex items-center">
              <!-- Vertical thumbnails for desktop -->
              <div class="mr8 hidden sm:block">
                <UiCarousel
                  orientation="vertical"
                  :opts="{
                    align: 'start',
                    startIndex: selectedIndex,
                  }"
                  class="w-24"
                  @init-api="(val) => emblaDialogThumbnailApi = val"
                >
                  <UiCarouselContent class="ml0 h-[600px] flex gap-2">
                    <UiCarouselItem
                      v-for="(asset, index) in assets" :key="`dialog-thumb-${index}`"
                      class="basis-auto cursor-pointer pl0"
                      @click="scrollTo(index, false, 'dialog')"
                    >
                      <div
                        class="relative border-2 n-border-base"
                        :class="index === dialogSelectedIndex ? 'border-orange' : 'op50'"
                      >
                        <NuxtImg
                          :src="asset.preview"
                          :alt="asset.alt"
                          width="100"
                          height="100"
                          placeholder
                          class="aspect-square hfull wfull select-none object-cover object-center"
                          loading="lazy"
                        />
                        <!-- Play badge for video thumbnails -->
                        <div
                          v-if="asset.type === AssetType.VIDEO"
                          class="absolute inset-0 flex items-center justify-center bg-black/30"
                        >
                          <div class="h-6 w-6 flex items-center justify-center rounded-full bg-black/70">
                            <svg
                              class="h-3 w-3 text-white"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </UiCarouselItem>
                  </UiCarouselContent>
                </UiCarousel>
              </div>

              <!-- Main detail carousel -->
              <div class="hauto wfull flex items-center justify-center sm:px12">
                <UiCarousel
                  class="relative mb4 max-w-4xl wfull"
                  :opts="{ duration: 20, startIndex: selectedIndex }"
                  @init-api="(val) => emblaDialogMainApi = val"
                >
                  <UiCarouselContent>
                    <UiCarouselItem v-for="(asset, index) in assets" :key="`dialog-${index}`">
                      <div class="relative aspect-square hfull wfull">
                        <NuxtImg
                          v-if="asset.type === AssetType.IMAGE"
                          :src="asset.preview"
                          :alt="asset.alt"
                          width="640"
                          height="640"
                          class="aspect-square hfull wfull select-none object-cover object-center"
                          preload
                        />
                        <video
                          v-if="asset.type === AssetType.VIDEO && asset.source"
                          :ref="(el) => setDialogVideoRef(el as HTMLVideoElement | null, index)"
                          class="pointer-events-auto absolute inset-0 aspect-square hfull wfull object-cover object-center"
                          :poster="asset.preview"
                          playsinline
                          webkit-playsinline
                          controls
                          muted
                          controlslist="nodownload noremoteplayback nofullscreen"
                          disablepictureinpicture=""
                          disableremoteplayback=""
                          x-webkit-airplay="deny"
                          @click="toggleVideo(index, true)"
                          @play="onVideoPlay(index, true)"
                          @pause="onVideoPause(index, true)"
                          @ended="onVideoEnded(index, true)"
                        >
                          <source :src="asset.source" :type="asset.mimeType">
                        </video>
                        <!-- Play button for video items -->
                        <button
                          v-if="asset.type === AssetType.VIDEO && !playingVideos.has(getVideoKey(index, true))"
                          class="absolute left-1/2 top-1/2 z-10 h-20 w-20 flex cursor-pointer items-center justify-center rounded-full bg-black/70 transition-colors -translate-x-1/2 -translate-y-1/2 hover:bg-black/80"
                          :aria-label="`Play video for ${asset.alt}`"
                          @click.stop="toggleVideo(index, true)"
                        >
                          <svg
                            class="h-7 w-7 text-white"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                          </svg>
                        </button>
                      </div>
                    </UiCarouselItem>
                  </UiCarouselContent>
                  <UiCarouselPrevious v-if="showNavigation" class="absolute top-1/2 bg-background hidden -left-16 !h12 !w12 sm:flex hover:n-button-hover">
                    <Icon name="i-heroicons:chevron-left" class="text-xl" />
                  </UiCarouselPrevious>
                  <UiCarouselNext v-if="showNavigation" class="absolute top-1/2 bg-background hidden -right-16 !h12 !w12 sm:flex hover:n-button-hover">
                    <Icon name="i-heroicons:chevron-right" class="text-xl" />
                  </UiCarouselNext>

                  <div v-if="showNavigation" class="absolute bottom-4 right-4 z10 rounded-full bg-slate-900/50 sm:hidden">
                    <span class="px2 text-context fw600 leading-6" n="sm white">
                      {{ dialogSelectedIndex + 1 }} / {{ assets?.length ?? 0 }}
                    </span>
                  </div>
                </UiCarousel>
              </div>
            </div>
          </div>
        </UiDialogContent>
      </UiDialog>
    </UiCarousel>

    <!-- Thumbnails for larger devices -->
    <UiCarousel
      class="relative wfull bg-gray-100 p2 hidden sm:block md:bg-white md:p0"
      :opts="{
        containScroll: 'keepSnaps',
        dragFree: true,
      }"
      @init-api="(val) => emblaThumbnailApi = val"
    >
      <UiCarouselContent class="ml0 flex gap-2">
        <UiCarouselItem v-for="(asset, index) in assets" :key="`thumb-${index}`" class="basis-[calc((100%-(5*0.5rem))/6)] cursor-pointer pl0" @click="scrollTo(index, false, 'thumbnail')">
          <div
            class="relative border-2 n-border-base rd-md"
            :class="index === selectedIndex ? 'border-error' : 'op50'"
          >
            <NuxtImg
              :src="asset.preview"
              :alt="asset.alt"
              width="100"
              height="100"
              placeholder
              class="aspect-square hfull wfull select-none select-none rd-md object-cover object-center"
              loading="lazy"
            />
            <!-- Play badge for video thumbnails -->
            <div
              v-if="asset.type === AssetType.VIDEO"
              class="absolute inset-0 flex items-center justify-center rounded-md bg-black/30"
            >
              <div class="h-6 w-6 flex items-center justify-center rounded-full bg-black/70">
                <svg
                  class="h-3 w-3 text-white"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
              </div>
            </div>
          </div>
        </UiCarouselItem>
      </UiCarouselContent>
    </UiCarousel>
  </div>
</template>
